import {
  buildEmailSourcePreviewText,
  encodeEmailSourceDataUrl,
} from '@/lib/email-conversion/source-formats/email-source-data-url'
import {
  detectEmailSourceFormatFromName,
  EMAIL_SOURCE_FILE_EXTENSIONS,
  EMAIL_SOURCE_MEDIA_TYPE_BY_FORMAT,
  type EmailSourceFormat,
  resolveEmailSourceFormat,
} from '@/lib/email-conversion/source-formats/email-source-format'
import {
  HTML_IMPORT_MAX_BYTES,
  htmlImportByteLength,
} from '@/lib/html-to-email/limits'

/**
 * The ONE place a browser reads an email source file. The result is an
 * inline attachment (a canonical `data:` URL) — never an upload — so an
 * `.html`/`.mjml`/`.eml`/`.jsx` file can ride a chat message or a dialog
 * submit without ever landing on the CDN.
 */

type StagedEmailSourceAttachment = {
  url: string
  mediaType: string
  filename: string
  previewText: string
  format: EmailSourceFormat
  byteLength: number
}

export class EmailSourceFileTooLargeError extends Error {
  constructor(readonly byteLength: number) {
    super(
      `email source file is ${byteLength} bytes; the inline limit is ${HTML_IMPORT_MAX_BYTES}`
    )
    this.name = 'EmailSourceFileTooLargeError'
  }
}

export function detectEmailSourceFile(
  file: Pick<File, 'name' | 'type'>
): EmailSourceFormat | undefined {
  return detectEmailSourceFormatFromName(file.name, file.type)
}

export async function stageEmailSourceAttachment(
  file: File
): Promise<StagedEmailSourceAttachment> {
  if (file.size > HTML_IMPORT_MAX_BYTES) {
    throw new EmailSourceFileTooLargeError(file.size)
  }
  const text = await file.text()
  const byteLength = htmlImportByteLength(text)
  if (byteLength > HTML_IMPORT_MAX_BYTES) {
    throw new EmailSourceFileTooLargeError(byteLength)
  }
  const format = resolveEmailSourceFormat({
    filename: file.name,
    mediaType: file.type,
    text,
  })
  const filename =
    file.name?.trim() || `email.${EMAIL_SOURCE_FILE_EXTENSIONS[format][0]}`
  return {
    url: encodeEmailSourceDataUrl(format, text),
    mediaType: EMAIL_SOURCE_MEDIA_TYPE_BY_FORMAT[format],
    filename,
    previewText: buildEmailSourcePreviewText(format, byteLength),
    format,
    byteLength,
  }
}
